import axiosClient from "./axiosClient";

const sanitizeLpe1Payload = (form) => {
  const payload = { ...(form || {}) };
  delete payload.signature_image_data_url;
  return payload;
};

/* -------------------------------------------------------------
   0. GET INITIAL FORM DATA (UNIT-BASED) - NO DB RECORD
   GET /units/:unit_id/lpe1/initial/
   Returns: { data: { ... } } - initial form data with calculated values
------------------------------------------------------------- */
export const getInitialLPE1Data = async (unitId) => {
  const res = await axiosClient.get(`/units/${unitId}/lpe1/initial/`);
  return res.data?.data || {};
};

export const getLPE1CheckboxSchema = async () => {
  const res = await axiosClient.get("/lpe1/checkbox-schema/");
  return res.data?.checkboxes || [];
};

export const getLPE1FormFieldSchema = async () => {
  const res = await axiosClient.get("/lpe1/form-field-schema/");
  return res.data?.fields || [];
};

/* -------------------------------------------------------------
   1. CREATE NEW LPE1 (UNIT-BASED)
   POST /units/:unit_id/lpe1/
   Returns: { lpe1_id: number }
------------------------------------------------------------- */
export const createLPE1 = (unitId) => {
  return axiosClient.post(`/units/${unitId}/lpe1/`);
};

/* -------------------------------------------------------------
   2. LOAD EXISTING LPE1 (BY LPE1 ID)
   GET /lpe1/:lpe1_id/
   Backend returns: { data: { ... }, unit_id, unit_name, unit_code, property_id, property_name }
------------------------------------------------------------- */
export const getLPE1ById = async (lpe1Id) => {
  const res = await axiosClient.get(`/lpe1/${lpe1Id}/`);

  // Return full response including unit/property info
  return {
    formData: res.data?.data || {},
    unitId: res.data?.unit_id,
    unitName: res.data?.unit_name,
    unitCode: res.data?.unit_code,
    propertyId: res.data?.property_id,
    propertyName: res.data?.property_name,
    status: res.data?.status,
    requestedDate: res.data?.requested_date,
    dueByDate: res.data?.due_by_date,
    createdAtUtc: res.data?.created_at_utc,
    updatedAtUtc: res.data?.updated_at_utc,
    rowVersion: res.data?.row_version,
  };
};

/* -------------------------------------------------------------
   3. SAVE LPE1 FORM (BY LPE1 ID)
   POST /lpe1/:lpe1_id/save/
------------------------------------------------------------- */
export const saveLPE1 = (lpe1Id, form, rowVersion) => {
  return axiosClient.post(`/lpe1/${lpe1Id}/save/`, {
    ...sanitizeLpe1Payload(form),
    row_version: rowVersion,
  });
};

/* -------------------------------------------------------------
   3b. UPDATE LPE1 META (STATUS/DUE DATE)
   POST /lpe1/:lpe1_id/meta/
------------------------------------------------------------- */
export const updateLPE1Meta = (lpe1Id, payload, rowVersion) => {
  return axiosClient.post(`/lpe1/${lpe1Id}/meta/`, {
    ...payload,
    row_version: rowVersion,
  });
};

/* -------------------------------------------------------------
   4. GENERATE EDITABLE PDF (BY LPE1 ID)
   ✅ NOW SUPPORTS ignore_property_mismatch
------------------------------------------------------------- */
export async function downloadEditableLpe1Pdf(lpe1Id, payload = {}) {
  const response = await axiosClient.post(
    `/lpe1/${lpe1Id}/pdf-editable/`,
    payload,
    { responseType: "blob" }
  );

  const blob = new Blob([response.data], {
    type: "application/pdf",
  });

  const url = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  const disposition = response.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  const filename = match ? decodeURIComponent(match[1]) : `LPE1-${lpe1Id}.pdf`;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/* -------------------------------------------------------------
   5. LIST LPE1 FORMS FOR A UNIT
------------------------------------------------------------- */
export const getLPE1ListForUnit = async (unitId) => {
  const res = await axiosClient.get(
    `/units/${unitId}/lpe1/list/`
  );
  return res.data;
};

/* -------------------------------------------------------------
   6. DELETE LPE1 FORM (BY LPE1 ID)
   DELETE /lpe1/:lpe1_id/delete/
------------------------------------------------------------- */
export const deleteLPE1 = (lpe1Id) => {
  return axiosClient.delete(`/lpe1/${lpe1Id}/delete/`);
};
